import nameModelValidator from 'forms/shared/validators/models/name/name';
import locationAddressModelValidator from 'forms/shared/validators/models/location/models/address';
import { hasYesOrNo } from 'models/validate';
import { DEFAULT_LATEST, TODAY } from 'constants/dateLimits';
import { ValidateJSModel } from 'forms/shared/types/validatejs';
import currencyModelValidator from 'forms/shared/validators/models/currency';
import { includesAny } from 'helpers/string/string';
import { getSafeDateTime, isSafeDateLater, setToStartOfMonth } from 'helpers/date';
import { SafeDateTime } from 'helpers/types';
import { isPVQForm } from 'helpers/form';
import { BANKRUPTCY_CHAPTER_VALUES_REQUIRING_TRUSTEE, bankruptcyChapterOptions } from 'constants/enums/financialOptions';
import { DateObject, DateTime } from 'luxon';
import name from 'forms/shared/validators/models/name/name';
import pvqNameModelValidator from 'forms/shared/validators/models/pvqName/name';
import { isSafeUnitedStates } from 'helpers/locations';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import { FinancialBankruptcyItemModel } from '../types';
import { pvqBankruptcyLocationValidator } from './pvqBankruptcyLocationValidator';

const petitionTypes = ['Chapter7', 'Chapter11', 'Chapter12', 'Chapter13'];

export const financialBankruptcyItemValidator: ValidateJSModel<FinancialBankruptcyItemModel> = {
  PetitionType: (value, attributes, attributeName, options) => {
    if (isPVQForm(options.formType)) {
      return {
        hasValue: {
          validator: {
            inclusion: bankruptcyChapterOptions as string[],
          },
        },
      };
    }

    return {
      hasValue: {
        validator: {
          inclusion: petitionTypes,
        },
      },
    };
  },

  CourtAddress: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        location: {
          validator: pvqBankruptcyLocationValidator,
        },
      };
    }

    return {
      presence: true,
      location: { validator: locationAddressModelValidator },
    };
  },

  CourtInvolved: { hasValue: true },

  CourtNumber: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return attributes.CourtNumberIdk?.applicable !== false ? {
        hasValue: true,
      } : {};
    }

    return { hasValue: true };
  },

  TotalAmount: {
    model: {
      validator: currencyModelValidator,
    },
  },

  DateFiled: (value, attributes, attributeName, options) => {
    const tempConfig: { FINANCIAL_RECORD_BANKRUPTCY: number, } = options?.formTypeConfig as { FINANCIAL_RECORD_BANKRUPTCY: number, };
    const bankruptcyRecordYears: number = tempConfig?.FINANCIAL_RECORD_BANKRUPTCY;
    const applicationSafeBirthdate: DateTime | SafeDateTime | DateObject = setToStartOfMonth(getSafeDateTime(options.applicantBirthdate)) as SafeDateTime;
    const applicant18Years: SafeDateTime | undefined = options?.applicantBirthdate
      && getSafeDateTime(getSafeDateTime(options.applicantBirthdate)?.datetime?.plus({ years: 18 }));
    const applicantEighteenthBirthdate: SafeDateTime | undefined = applicant18Years && setToStartOfMonth(applicant18Years) as SafeDateTime;
    const requiredDate: SafeDateTime = setToStartOfMonth(getSafeDateTime(TODAY.minus({ years: bankruptcyRecordYears }))) as SafeDateTime;
    let earliestDate: DateTime | undefined;
    let earliestMessage: string;

    if (options.formType && options.formType === 'SF86' && applicantEighteenthBirthdate?.valid && requiredDate?.valid && isSafeDateLater(requiredDate, applicantEighteenthBirthdate)) {
      earliestDate = applicantEighteenthBirthdate.datetime;
      earliestMessage = 'error.beforeDuration';
    } else if (applicationSafeBirthdate.valid && requiredDate?.valid && isSafeDateLater(requiredDate, applicationSafeBirthdate)) {
      earliestDate = requiredDate.datetime;
      earliestMessage = 'error.beforeDuration';
    } else {
      earliestDate = applicationSafeBirthdate.datetime;
      earliestMessage = 'error.applicantMin';
    }

    return {
      presence: true,
      date: {
        requireDay: false,
        earliest: earliestDate,
        tooEarly: earliestMessage,
        latest: DEFAULT_LATEST,
      },
    };
  },

  DateDischarged: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.HasDischargeExplanation?.value === 'No') { return {}; }

    if (!isPVQForm(options?.formType) && attributes?.DateDischargedNotApplicable?.applicable === false) { return {}; }

    return {
      presence: true,
      date: {
        requireDay: false,
        latest: DEFAULT_LATEST,
        earliest: attributes?.DateFiled,
        tooEarly: 'error.date.bankruptcyDischargeDateTooEarly',
      },
    };
  },

  NameDebt: {
    presence: true,
    model: { validator: nameModelValidator },
  },

  // TODO: HasDischargeExplanation is poorly named
  // It should be something along the lines of WasBankruptcyDebtsDischarged
  HasDischargeExplanation: {
    hasValue: { validator: hasYesOrNo },
  },

  DischargeExplanation: (value, attributes, attributeName, options) => (isPVQForm(options?.formType) && attributes?.HasDischargeExplanation?.value === 'No' ? { hasValue: true } : {}),
  PvqTrustee: (value, attributes, attributeName, options) => (isPVQForm(options?.formType)
      && BANKRUPTCY_CHAPTER_VALUES_REQUIRING_TRUSTEE.includes(attributes?.PetitionType?.value as string)
      && attributes.TrusteeIdk?.applicable !== false
      && attributes.PvqTrusteeNameNotApplicable?.applicable !== false
    ? {
      presence: true,
      model: { validator: nameModelValidator },
    }
    : {}), // SF case is handled with Trustee field
  // requireFinancialBankruptcyTrusteeInformation is used for the SF85P
  Trustee: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) { return {}; } // PVQ case is handled with PvqTrustee field

    const { PetitionType } = attributes;
    const { requireFinancialBankruptcyTrusteeInformation } = options;
    const rules = { hasValue: true };

    if (attributes?.TrusteeNotApplicable?.applicable === false) { return {}; }
    if (
      requireFinancialBankruptcyTrusteeInformation
      && includesAny(PetitionType?.value ?? '', ['Chapter12', 'Chapter13'])
    ) {
      return rules;
    }

    if (PetitionType?.value === 'Chapter13') {
      return rules;
    }

    return {};
  },

  TrusteeAddress: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && BANKRUPTCY_CHAPTER_VALUES_REQUIRING_TRUSTEE.includes(attributes?.PetitionType?.value as string)
      && attributes?.TrusteeAddressNotApplicable?.applicable !== false) {
      return {
        location: {
          requireIsMilitaryDiplomaticAddress: false,
          requireStreet: isSafeUnitedStates(value),
          validator: pvqLocationModelValidator,

        },
        presence: true,
      };
    }

    const { PetitionType } = attributes;
    // requireFinancialBankruptcyTrusteeInformation is used for the SF85P
    const { requireFinancialBankruptcyTrusteeInformation } = options;
    const rules = { presence: true, location: { validator: locationAddressModelValidator } };

    if (attributes?.TrusteeAddressNotApplicable?.applicable === false) { return {}; }

    if (
      requireFinancialBankruptcyTrusteeInformation
      && includesAny(PetitionType?.value ?? '', ['Chapter12', 'Chapter13'])
    ) {
      return rules;
    }

    if (PetitionType?.value === 'Chapter13') { return rules; }

    return {};
  },
  DateDischargedNotApplicable: {},

  TotalAmountEstimated: {},
  TrusteeNotApplicable: {},
  TrusteeAddressNotApplicable: {},
  PvqTrusteeNameNotApplicable: {},

  WasFiledJointly: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        hasValue: {
          validator: hasYesOrNo,
        },
      };
    }

    return {};
  },
  JointFileName: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.WasFiledJointly?.value === 'Yes') {
      return { presence: true, model: { validator: pvqNameModelValidator, requireMiddleName: false } };
    }

    return {};
  },
};
